// ft3d-inventory-windows.ts — floating inventory windows for the FT3D campaign surface.
//
// The legacy XZ widget originally owned this manager. FT3D still renders the same PC-sheet panes
// (master_control / equipment_window / onhand_window / bag_window), so keep the window lifecycle in
// the encounter-map bundle while removing any dependency on the retired XZ widget runtime.

// v2: the master control moved from a right-edge anchor to a left one and the section cascade
// flipped with it. A viewer holding v1 positions would keep the old layout for the life of the tab
// (sessionStorage survives reloads), so the key is versioned with the layout contract it describes.
const POSITION_KEY = 'ft_bagwin_pos_v2';
let zTop = 19996;

type Position = { x: number; y: number };
type HostBounds = { left: number; top: number; right: number; bottom: number };

declare global {
    interface Window {
        ftMountPcSheet?: (root?: ParentNode) => void;
        ftToggleInventoryWindows?: (casterId: string, force?: boolean) => boolean;
        ftInventoryWindowsOpen?: (casterId: string) => boolean;
        ftToggleInvSection?: (casterId: string, section: string) => void;
        ftIsInvSectionOpen?: (casterId: string, section: string) => boolean;
        ftAutoSortInvWindows?: (casterId: string) => void;
        ftCloseInvSection?: (casterId: string, section: string) => void;
        /** Close the WHOLE inventory surface (master + every section/bag). True when it closed something. */
        ftCloseAllInventoryWindows?: () => boolean;
    }
}

function attr(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

class Ft3dInventoryWindowManager {
    private windows = new Map<string, HTMLElement>();
    private cascade = 0;

    private host(): HTMLElement | null {
        return document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view].is-active');
    }

    private hostBounds(host: HTMLElement): HostBounds {
        const rect = host.getBoundingClientRect();
        const left = Number.isFinite(rect.left) ? Math.max(0, rect.left) : 0;
        const top = Number.isFinite(rect.top) ? Math.max(0, rect.top) : 0;
        const right = Number.isFinite(rect.right) && rect.right > left
            ? Math.min(window.innerWidth, rect.right)
            : window.innerWidth;
        const bottom = Number.isFinite(rect.bottom) && rect.bottom > top
            ? Math.min(window.innerHeight, rect.bottom)
            : window.innerHeight;
        return { left, top, right, bottom };
    }

    private loadPositions(): Record<string, Position> {
        try {
            const parsed = JSON.parse(sessionStorage.getItem(POSITION_KEY) || '{}');
            return parsed && typeof parsed === 'object' ? parsed as Record<string, Position> : {};
        } catch {
            return {};
        }
    }

    private savePosition(key: string, x: number, y: number): void {
        const positions = this.loadPositions();
        positions[key] = { x: Math.round(x), y: Math.round(y) };
        try { sessionStorage.setItem(POSITION_KEY, JSON.stringify(positions)); } catch { /* private mode */ }
    }

    private clampToHost(win: HTMLElement, host: HTMLElement, key?: string): void {
        const bounds = this.hostBounds(host);
        const margin = 8;
        const minX = bounds.left + margin;
        const minY = bounds.top + margin;
        const maxX = Math.max(minX, bounds.right - win.offsetWidth - margin);
        const maxY = Math.max(minY, bounds.bottom - win.offsetHeight - margin);
        const rawX = parseFloat(win.style.left || String(minX));
        const rawY = parseFloat(win.style.top || String(minY));
        const x = Math.min(maxX, Math.max(minX, Number.isFinite(rawX) ? rawX : minX));
        const y = Math.min(maxY, Math.max(minY, Number.isFinite(rawY) ? rawY : minY));
        win.style.left = `${x}px`;
        win.style.top = `${y}px`;
        win.style.right = 'auto';
        if (key && (x !== rawX || y !== rawY)) this.savePosition(key, x, y);
    }

    private bringToFront(win: HTMLElement): void {
        if (zTop >= 19998) {
            const others = Array.from(this.windows.values())
                .filter((candidate) => candidate !== win)
                .sort((a, b) => parseInt(a.style.zIndex || '0', 10) - parseInt(b.style.zIndex || '0', 10));
            let z = 19996;
            for (const other of others) other.style.zIndex = String(Math.min(z++, 19997));
            zTop = 19997;
        }
        win.style.zIndex = String(++zTop);
    }

    isMasterOpen(casterId: string): boolean {
        return Boolean(casterId && this.windows.has(`master:${casterId}`));
    }

    /** ESC treats the inventory as ONE surface (owner 2026-09-03): dismissing it from the keyboard
     *  closes the master AND every section/bag, whichever of them happened to be the active window.
     *  Routed through closeAllAndNotify so the sheet/dock inventory pills un-press exactly as they do
     *  when the master's own × is clicked. Returns false when nothing was open, so the caller can fall
     *  through to the ordinary active-window shortcut. */
    closeEverything(): boolean {
        if (!this.windows.size) return false;
        // Collect from BOTH key shapes. A section can outlive its master (a master close racing a
        // section open), and its caster's inventory pills would otherwise stay pressed over a surface
        // that is no longer on screen. Close ONCE, then notify each caster that had anything open.
        const casters = new Set<string>();
        for (const key of this.windows.keys()) {
            if (key.startsWith('master:')) casters.add(key.slice('master:'.length));
            else if (key.startsWith('section:')) {
                const rest = key.slice('section:'.length);
                const end = rest.indexOf(':');
                casters.add(end === -1 ? rest : rest.slice(0, end));
            }
        }
        this.closeAll();
        for (const casterId of casters) {
            if (!casterId) continue;
            window.dispatchEvent(new CustomEvent('ft:inventory-windows-changed', {
                detail: { viewerId: casterId, open: false },
            }));
        }
        return true;
    }

    toggleMaster(casterId: string, force?: boolean): boolean {
        if (!casterId) return false;
        const shouldOpen = typeof force === 'boolean' ? force : !this.isMasterOpen(casterId);
        if (!shouldOpen) {
            this.closeAllAndNotify(casterId);
            return false;
        }

        // Inventory windows represent the currently-driven actor. Switching actors replaces the prior
        // set instead of leaving two unrelated masters and their bags layered over the encounter.
        const priorMaster = Array.from(this.windows.keys()).find((key) => key.startsWith('master:'));
        if (priorMaster && priorMaster !== `master:${casterId}`) {
            this.closeAllAndNotify(priorMaster.slice('master:'.length));
        }
        if (!this.openMaster(casterId)) return false;
        window.dispatchEvent(new CustomEvent('ft:inventory-windows-changed', {
            detail: { viewerId: casterId, open: true },
        }));
        return true;
    }

    private openMaster(casterId: string, forLoot = false): boolean {
        const key = `master:${casterId}`;
        const existing = this.windows.get(key);
        if (existing) {
            const host = this.host();
            if (!host) return false;
            this.clampToHost(existing, host, key);
            this.bringToFront(existing);
            return true;
        }

        const id = attr(casterId);
        const pane = `<div class="ft-pc-sheet-pane" data-ft-pc-pane="master_control" `
            + `data-ft-caster-id="${id}" data-ft-viewer-caster-id="${id}"></div>`;
        if (!this.createWindow(key, 'Inventory', pane, () => this.closeAllAndNotify(casterId))) return false;

        let sections = this.loadOpenSet(casterId);
        if (sessionStorage.getItem(this.openSetKey(casterId)) === null) {
            const bagSections = Array.from(
                this.windows.get(key)?.querySelectorAll<HTMLElement>('[data-ft-section^="bag:"]') ?? [],
            ).map((tile) => tile.dataset.ftSection ?? '').filter(Boolean);
            sections = new Set(forLoot ? bagSections : ['equipment', 'onhand', ...bagSections]);
            this.saveOpenSet(casterId, sections);
        }
        for (const section of sections) this.openSection(casterId, section);

        if (!forLoot && sessionStorage.getItem(this.layoutSeedKey(casterId)) === null) {
            try { sessionStorage.setItem(this.layoutSeedKey(casterId), '1'); } catch { /* private mode */ }
            window.requestAnimationFrame(() => this.autoSort(casterId));
        }
        return true;
    }

    toggleSection(casterId: string, section: string): void {
        if (!casterId || !section) return;
        if (this.windows.has(this.sectionWindowKey(casterId, section))) this.closeSection(casterId, section);
        else this.openSection(casterId, section);
    }

    openSection(casterId: string, section: string): void {
        if (!casterId || !section) return;
        const key = this.sectionWindowKey(casterId, section);
        if (!this.windows.has(key)) {
            const definition = this.sectionPane(casterId, section);
            if (!definition || !this.createWindow(key, definition.title, definition.pane, () => this.closeSection(casterId, section))) {
                return;
            }
        }
        const sections = this.loadOpenSet(casterId);
        sections.add(section);
        this.saveOpenSet(casterId, sections);
        this.highlightSectionTile(casterId, section, true);
    }

    closeSection(casterId: string, section: string): void {
        this.closeWindow(this.sectionWindowKey(casterId, section));
        const sections = this.loadOpenSet(casterId);
        sections.delete(section);
        this.saveOpenSet(casterId, sections);
        this.highlightSectionTile(casterId, section, false);
    }

    isSectionOpen(casterId: string, section: string): boolean {
        return this.loadOpenSet(casterId).has(section);
    }

    /** Lay the section windows out around the master control. The master anchors LEFT (see
     *  masterAnchorX), so the sections flow to its RIGHT and wrap against the host's right edge —
     *  the mirror of the old layout, which reserved the right for a right-pinned master. */
    autoSort(casterId: string): void {
        const host = this.host();
        if (!host) return;
        const bounds = this.hostBounds(host);
        const prefix = `section:${casterId}:`;
        const margin = 12;
        const gap = 12;
        const master = this.windows.get(`master:${casterId}`);
        const masterRect = master?.getBoundingClientRect();
        // Start to the RIGHT of the master — but only when a readable strip actually fits there.
        // Capping the start at `bounds.right - 260` unconditionally would put it INSIDE the master on
        // a narrow host or after the master was dragged right; fall back to the host's left margin in
        // that case, so the sections sit beside the master rather than under its own controls.
        const rightOfMaster = masterRect && masterRect.width > 0 ? masterRect.right + gap : null;
        const rightmostUsableStart = Math.max(bounds.left + margin, bounds.right - 260);
        const startX = rightOfMaster !== null && rightOfMaster <= rightmostUsableStart
            ? rightOfMaster
            : bounds.left + margin;
        let x = startX;
        let y = bounds.top + 64;
        let rowHeight = 0;
        for (const [key, win] of this.windows) {
            if (!key.startsWith(prefix)) continue;
            if (x > startX && x + win.offsetWidth > bounds.right - margin) {
                x = startX;
                y += rowHeight + gap;
                rowHeight = 0;
            }
            // A tall section (Equipment is ~700px) pushes the next row past the bottom of the view.
            // Overlapping a little beats a window the viewer cannot reach, so clamp every placement
            // into the host rather than trusting the flow position.
            const placedX = Math.min(x, Math.max(bounds.left + margin, bounds.right - win.offsetWidth - margin));
            const placedY = Math.min(y, Math.max(bounds.top + margin, bounds.bottom - win.offsetHeight - margin));
            win.style.left = `${placedX}px`;
            win.style.top = `${placedY}px`;
            win.style.right = 'auto';
            this.savePosition(key, placedX, placedY);
            this.bringToFront(win);
            x += win.offsetWidth + gap;
            rowHeight = Math.max(rowHeight, win.offsetHeight);
        }
    }

    private sectionPane(casterId: string, section: string): { title: string; pane: string } | null {
        const id = attr(casterId);
        const base = `data-ft-caster-id="${id}" data-ft-viewer-caster-id="${id}"`;
        if (section === 'equipment') {
            return { title: 'Equipment', pane: `<div class="ft-pc-sheet-pane" data-ft-pc-pane="equipment_window" ${base}></div>` };
        }
        if (section === 'onhand') {
            return { title: 'On Hand', pane: `<div class="ft-pc-sheet-pane" data-ft-pc-pane="onhand_window" ${base}></div>` };
        }
        if (!section.startsWith('bag:')) return null;
        const containerId = attr(section.slice(4));
        return {
            title: 'Bag',
            pane: `<div class="ft-pc-sheet-pane" data-ft-pc-pane="bag_window" ${base} data-ft-bag-container-id="${containerId}"></div>`,
        };
    }

    private openSetKey(casterId: string): string { return `ft_invwin_open:${casterId}`; }
    private layoutSeedKey(casterId: string): string { return `ft_invwin_layout_seeded_v2:${casterId}`; }
    private sectionWindowKey(casterId: string, section: string): string { return `section:${casterId}:${section}`; }

    private loadOpenSet(casterId: string): Set<string> {
        try {
            const parsed = JSON.parse(sessionStorage.getItem(this.openSetKey(casterId)) || '[]');
            return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []);
        } catch {
            return new Set();
        }
    }

    private saveOpenSet(casterId: string, sections: Set<string>): void {
        try { sessionStorage.setItem(this.openSetKey(casterId), JSON.stringify(Array.from(sections))); } catch { /* private mode */ }
    }

    private highlightSectionTile(casterId: string, section: string, open: boolean): void {
        const master = this.windows.get(`master:${casterId}`);
        const tile = Array.from(master?.querySelectorAll<HTMLElement>('[data-ft-section]') ?? [])
            .find((candidate) => candidate.dataset.ftSection === section);
        tile?.classList.toggle('is-open', open);
    }

    private closeAllAndNotify(casterId: string): void {
        this.closeAll();
        if (casterId) {
            window.dispatchEvent(new CustomEvent('ft:inventory-windows-changed', {
                detail: { viewerId: casterId, open: false },
            }));
        }
    }

    private closeAll(): void {
        for (const win of this.windows.values()) win.remove();
        this.windows.clear();
        this.cascade = 0;
    }

    private closeWindow(key: string): void {
        this.windows.get(key)?.remove();
        this.windows.delete(key);
    }

    openWorldContainer(id: string, title: string): HTMLElement | null {
        const key = `world-container:${id}`;
        if (!this.windows.has(key)) {
            if (!this.createWindow(key, title, '<div data-ft-world-container-body></div>', () => this.closeWindow(key))) return null;
        }
        const win = this.windows.get(key)!;
        win.dataset.ftWorldContainer = id;
        const heading = win.querySelector<HTMLElement>('.ft-bagwin__title');
        if (heading && !heading.querySelector('[data-ft-loot-identity]')) heading.textContent = title;
        this.bringToFront(win);
        return win.querySelector<HTMLElement>('[data-ft-world-container-body]');
    }

    closeWorldContainers(): void {
        for (const key of this.windows.keys()) if (key.startsWith('world-container:')) this.closeWindow(key);
    }

    lootDestinations(casterId: string): Array<{ id: string; name: string }> {
        const master = this.windows.get(`master:${casterId}`);
        const bags = Array.from(master?.querySelectorAll<HTMLElement>('[data-ft-section^="bag:"]') ?? [])
            .map(tile => ({ id: (tile.dataset.ftSection ?? '').slice(4), name: tile.querySelector('.ft-mastertile-label')?.textContent?.trim() || 'Bag' }));
        return casterId ? [...bags, { id: casterId, name: 'On Hand' }] : [];
    }

    prepareLoot(casterId: string): void {
        if (!casterId) return;
        const prior = Array.from(this.windows.keys()).find(key => key.startsWith('master:'));
        if (prior && prior !== `master:${casterId}`) this.closeAllAndNotify(prior.slice(7));
        if (!this.openMaster(casterId, true)) return;
        const bags = this.lootDestinations(casterId).filter(destination => destination.id !== casterId);
        for (const bag of bags) this.openSection(casterId, `bag:${bag.id}`);
        if (!bags.length) this.openSection(casterId, 'onhand');
        window.dispatchEvent(new CustomEvent('ft:inventory-windows-changed', { detail: { viewerId: casterId, open: true } }));
    }

    arrangeLoot(id: string, casterId: string): void {
        const host = this.host();
        if (!host) return;
        const bounds = this.hostBounds(host), gap = 12;
        type Rect = { left: number; top: number; right: number; bottom: number };
        const occupied: Rect[] = [];
        // Keep the sheet and other floating HUD windows usable as well as the bags.
        document.querySelectorAll<HTMLElement>('[data-ft3d-window]').forEach(el => {
            const rect = el.getBoundingClientRect();
            if (!el.hidden && rect.width && rect.height && getComputedStyle(el).visibility !== 'hidden') occupied.push(rect);
        });
        const lootKey = `world-container:${id}`;
        const priority = (key: string): number => key === lootKey ? 0 : key.startsWith(`section:${casterId}:bag:`) ? 1
            : key === `section:${casterId}:onhand` ? 2 : key === `master:${casterId}` ? 3 : 4;
        const windows = [...this.windows].sort(([a], [b]) => priority(a) - priority(b));
        for (const [key, win] of windows) {
            const width = win.offsetWidth, height = win.offsetHeight;
            const minX = bounds.left + gap, minY = bounds.top + gap;
            const maxX = Math.max(minX, bounds.right - width - gap), maxY = Math.max(minY, bounds.bottom - height - gap);
            const clampX = (x: number) => Math.max(minX, Math.min(maxX, x));
            const clampY = (y: number) => Math.max(minY, Math.min(maxY, y));
            const xs = [minX, maxX], ys = [clampY(bounds.top + 64), minY, maxY];
            for (const rect of occupied) {
                xs.push(clampX(rect.right + gap), clampX(rect.left - width - gap));
                ys.push(clampY(rect.bottom + gap), clampY(rect.top - height - gap));
            }
            let best = { x: minX, y: minY, score: Infinity };
            for (const x of xs) for (const y of ys) {
                const overlap = occupied.reduce((sum, rect) => sum
                    + Math.max(0, Math.min(x + width + gap, rect.right) - Math.max(x - gap, rect.left))
                    * Math.max(0, Math.min(y + height + gap, rect.bottom) - Math.max(y - gap, rect.top)), 0);
                const score = overlap * 10000 + Math.abs(y - (bounds.top + 64)) * 2 + (x - minX);
                if (score < best.score) best = { x, y, score };
            }
            win.style.left = `${best.x}px`; win.style.top = `${best.y}px`; win.style.right = 'auto';
            occupied.push({ left: best.x, top: best.y, right: best.x + width, bottom: best.y + height });
        }
        const loot = this.windows.get(lootKey);
        if (loot) this.bringToFront(loot);
    }

    private createWindow(key: string, fallbackTitle: string, paneHtml: string, onClose: () => void): boolean {
        const host = this.host();
        if (!host) return false;

        const win = document.createElement('div');
        win.className = 'ft-bagwin';
        win.dataset.ftBagwinKey = key;
        win.innerHTML = `<div class="ft-bagwin__bar">`
            + `<span class="ft-bagwin__title">${attr(fallbackTitle)}</span>`
            + `<button type="button" class="ft-window-close ft-bagwin__close" aria-label="Close window">&times;</button>`
            + `</div><div class="ft-bagwin__body">${paneHtml}</div>`;
        host.appendChild(win);

        const saved = this.loadPositions()[key];
        const bounds = this.hostBounds(host);
        if (saved) {
            win.style.left = `${saved.x}px`;
            win.style.top = `${saved.y}px`;
        } else {
            const offset = 72 + (this.cascade % 6) * 30;
            win.style.left = `${bounds.left + offset}px`;
            win.style.top = `${bounds.top + offset}px`;
            this.cascade++;
        }
        win.style.zIndex = String(++zTop);
        this.windows.set(key, win);

        const body = win.querySelector<HTMLElement>('.ft-bagwin__body');
        if (body) window.ftMountPcSheet?.(body);

        const renderedTitle = win.querySelector('[data-ft-charname]')?.getAttribute('data-ft-charname')?.trim()
            || win.querySelector('.ft-container-header-name')?.textContent?.trim();
        if (renderedTitle) {
            const title = win.querySelector<HTMLElement>('.ft-bagwin__title');
            if (title) title.textContent = renderedTitle;
        }

        if (key.startsWith('master:') && !saved) {
            win.style.left = `${this.masterAnchorX(bounds, win)}px`;
            win.style.top = `${bounds.top + 64}px`;
        }
        this.clampToHost(win, host, saved ? key : undefined);

        win.querySelector('.ft-bagwin__close')?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
        });
        win.addEventListener('mousedown', () => this.bringToFront(win), true);
        const bar = win.querySelector<HTMLElement>('.ft-bagwin__bar');
        if (bar) this.attachDrag(win, bar, key);
        return true;
    }

    /** Where a FRESH master control lands (owner 2026-09-03). It used to pin to the far RIGHT edge,
     *  which put the one window you actually drive as far from the sections as the screen allows and
     *  left the cascade nowhere to grow but off-screen. Anchor it LEFT instead, so Equipment / On Hand
     *  / bags fan out to its right and the toggle tiles sit beside the windows they toggle.
     *  The actor sheet owns the left column when it is open, so start just past its right edge rather
     *  than underneath it; with no sheet open this is simply the host's left margin. A remembered
     *  drag position still wins — this only decides the first placement. */
    private masterAnchorX(bounds: HostBounds, win: HTMLElement): number {
        const margin = 12;
        // Only the CANONICAL actor sheet is the left column; an inspected sheet is
        // data-ft3d-window="inspected-sheet" and is user-positioned, so it never claims this space.
        const sheet = document.querySelector<HTMLElement>('[data-ft3d-window="actor"]');
        const sheetRect = sheet && !sheet.hidden && sheet.offsetParent !== null
            ? sheet.getBoundingClientRect()
            : null;
        // A real intersection test against the host's left band — not a width-relative one, which a
        // wide sheet sitting well inside the host would satisfy by accident.
        const leftBand = bounds.left + Math.max(64, (bounds.right - bounds.left) * 0.25);
        const ownsLeftColumn = !!sheetRect
            && sheetRect.width > 0
            && sheetRect.left <= leftBand
            && sheetRect.right > bounds.left;
        const past = ownsLeftColumn && sheetRect ? sheetRect.right + margin : bounds.left + margin;
        const furthestRight = Math.max(bounds.left + margin, bounds.right - win.offsetWidth - margin);
        return Math.min(Math.max(bounds.left + margin, past), furthestRight);
    }

    private attachDrag(win: HTMLElement, bar: HTMLElement, key: string): void {
        bar.addEventListener('pointerdown', (event: PointerEvent) => {
            if ((event.target as HTMLElement | null)?.closest('button, select, input, a')) return;
            event.preventDefault();
            this.bringToFront(win);
            const rect = win.getBoundingClientRect();
            const offsetX = event.clientX - rect.left;
            const offsetY = event.clientY - rect.top;
            const move = (moveEvent: PointerEvent) => {
                const x = Math.max(0, Math.min(moveEvent.clientX - offsetX, window.innerWidth - 48));
                const y = Math.max(0, Math.min(moveEvent.clientY - offsetY, window.innerHeight - 24));
                win.style.left = `${x}px`;
                win.style.top = `${y}px`;
                win.style.right = 'auto';
            };
            const up = () => {
                document.removeEventListener('pointermove', move);
                document.removeEventListener('pointerup', up);
                const finalRect = win.getBoundingClientRect();
                this.savePosition(key, finalRect.left, finalRect.top);
            };
            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', up);
        });
    }
}

const inventoryWindows = new Ft3dInventoryWindowManager();

Object.assign(window, {
    ftCreateWorldContainerWindow: (id: string, title: string) => inventoryWindows.openWorldContainer(id, title),
    ftCloseWorldContainers: () => inventoryWindows.closeWorldContainers(),
    ftPrepareLootWindows: (casterId: string) => inventoryWindows.prepareLoot(casterId),
    ftLootDestinations: (casterId: string) => inventoryWindows.lootDestinations(casterId),
    ftArrangeLootWindows: (id: string, casterId: string) => inventoryWindows.arrangeLoot(id, casterId),
    ftToggleInventoryWindows: (casterId: string, force?: boolean) => inventoryWindows.toggleMaster(casterId, force),
    ftInventoryWindowsOpen: (casterId: string) => inventoryWindows.isMasterOpen(casterId),
    ftToggleInvSection: (casterId: string, section: string) => inventoryWindows.toggleSection(casterId, section),
    ftIsInvSectionOpen: (casterId: string, section: string) => inventoryWindows.isSectionOpen(casterId, section),
    ftAutoSortInvWindows: (casterId: string) => inventoryWindows.autoSort(casterId),
    ftCloseInvSection: (casterId: string, section: string) => inventoryWindows.closeSection(casterId, section),
    ftCloseAllInventoryWindows: () => inventoryWindows.closeEverything(),
});

window.addEventListener('ft:active-encounter-change', () => inventoryWindows.closeWorldContainers());

export {};
